import React, { useState, useEffect } from 'react';
import { ReactComponent as Copy } from '../assets/copy.svg';
import { ReactComponent as Clear } from '../assets/reset.svg';
import { ReactComponent as Next } from '../assets/next.svg';
import { ReactComponent as NameAlert } from '../assets/nameAlert.svg';
import '../styles/Summary.css';
import { API_ENDPOINT } from '../config';
import { getAvatar } from '../utils/commonFunctions';
import useAutoScroll from '../hooks/useAutoScroll';
import Loader from './Loader';
import ThinkingComponent from './ThinkingComponent';
import { storage as localStorage } from '../utils/storage';

import { Message } from '../types/message';
import { PromptOption } from '../types/prompt';
import { getPromptOptions } from '../services/chatService';


interface ChatComponentProps {
  initialContent?: string; // Optional initial content prop for user message
}

export default function Summary({ initialContent = '', disabled= false}) {
  const [conversationHistory, setConversationHistory] = useState<Message[]>([]);
  const [userMessage, setUserMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatStarted, setChatStarted] = useState(false);
  const summaryRef = useAutoScroll([conversationHistory, isLoading]);
  const [avatar, setAvatar] = useState<React.ReactNode>(null);
  const [selectedPromptValue, setSelectedPromptValue] = useState<string>('');
  
  const [dropdownOptions, setDropdownOptions] = useState<PromptOption[]>([]);

  useEffect(() => {
    const fetchPromptOptions = async () => {
      try {
        const options = await getPromptOptions();
        setDropdownOptions(options);
      } catch (error) {
        console.error('Error fetching prompt options:', error);
      } 
    };

    fetchPromptOptions();
  }, []);

  useEffect(() => {
    const fetchAvatar = async () => {
      const avatar = await getAvatar();
      setAvatar(avatar);
    };
    fetchAvatar();
  }, []);

  const [isOpen, setIsOpen] = useState(false);

  const handleSubmit = () => {
    setChatStarted(true);
    if (!disabled) {
      const messageKey = userMessage.trim();
      const messageValue = selectedPromptValue || messageKey; // Use value if dropdown selected
  
      if (messageValue) {
        handleUserMessage({ key: messageKey, value: messageValue });
        setSelectedPromptValue('');
        setUserMessage('');
      }
    }
  };

  const handleCopyToClipboard = () => {
    const formattedText = conversationHistory
      .map((msg) => `${msg.role}: ${msg.content}`)
      .join('\n');
    navigator.clipboard.writeText(formattedText).then(() => {
      alert('Conversation history copied to clipboard!');
    }).catch((err) => {
      console.error('Failed to copy text: ', err);
    });
  };

  const handleClearConversation = () => {
    setConversationHistory([]);
    setChatStarted(false);
  };

  useEffect(() => {
    if (initialContent && !chatStarted) {
      setConversationHistory([
        { role: 'user', content: initialContent },
      ]);
    }
  }, [initialContent, chatStarted]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleSubmit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSubmit]);

  const handleUserMessage = async (message: { key: string; value: string }) => {
    try {
      const token = await localStorage.get('accessToken');
      if (!token) {
        alert('You must be logged in to chat!');
        return;
      }
  
      setIsLoading(true);
      const response = await fetch(API_ENDPOINT.CHAT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          conversationHistory, 
          userMessage: message.value // Send value to API
        }),
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data: { assistantReply: string } = await response.json();
  
      setIsLoading(false);
  
      setConversationHistory((prevHistory) => [
        ...prevHistory,
        { role: 'user', key: message.key, content: message.value }, // Store key in UI, value hidden
        { role: 'assistant', key: '', content: data.assistantReply },
      ]);
  
      setUserMessage('');
    } catch (error) {
      setIsLoading(false);
      console.error('Error in chat:', error);
      alert('Failed to send the message. Please try again.');
    }
  };

  // Handle selecting a dropdown option
  const handleDropdownSelect = (option: PromptOption) => {

    setUserMessage(option.key);  // Show key in input
    setSelectedPromptValue(option.value); // Store full prompt
    setIsOpen(false);

  };

  return (
    <div className="summary">
      <div className="summary-content">
        <div className="action-buttons">
          <button className="action-button" onClick={handleCopyToClipboard}>
            <Copy />
          </button>
          <button className="action-button" onClick={handleClearConversation}>
            <Clear />
          </button>
        </div>
        <div className="line" />
        <div className="content" ref={summaryRef}>
          {conversationHistory.length >= 2 ?  conversationHistory.map((msg, index) => (

            index !== 0 && <div key={index}>
              {msg.role === 'user' ? (
                <div key={index}>
                  <div className="user">
                    <div className="avatar avatar-mubah">{avatar}</div>
                    <div className="user-line-left"></div>
                  </div>
                  <p>{msg.key}</p>
                </div>
              ) : (
                <div key={index}>
                  <div className="user right">
                    <div className="user-line-right"></div>
                    <NameAlert />
                  </div>
                  {msg.content.split("\n").map((line, index) => (
                    <p key={index} dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") }}></p>
                  ))}
                </div>
              )}
            </div>
          )): <p style={{color: 'rgb(128, 128, 128)'}}>Start a conversation with the assistant to get a summary</p>}
           {isLoading && <ThinkingComponent />}
        </div>
      </div>
      <div  className="summary-controls">
        <div className="dropdown">
          <button
            disabled={disabled || isLoading}
            className="dropdown-button"
            onClick={() => setIsOpen(!isOpen)}
          >
            <input
              disabled={disabled || isLoading}
              value={userMessage}
              placeholder='Type a message...'
              onChange={(e) => setUserMessage(e.target.value)}
            />
          </button>
          {isOpen && (
            <div className="dropdown-content">
            {dropdownOptions.map((option: PromptOption) => (
              <button
                onClick={() => handleDropdownSelect(option)}
                className="dropdown-item"
                key={option.key}
              >
                {option.key}
              </button>
            ))}
          </div>
          )}
        </div>

        <button onClick={() => handleSubmit()} disabled={disabled || isLoading} className="submit-button">
          <Next />
        </button>
      </div>
    </div>
  );
}
